import { type CSSProperties, type ReactNode, useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { api } from '../../api/client';

/**
 * Electronic-signature attestation popup (ADR-0009) — "Validate & Sign" /
 * "Approve & Sign".  Re-auth = password (always) + a second factor selected in
 * Default Settings (Authenticator / Email code / SMS code) + an "I certify…"
 * checkbox.  Two API calls: challenge (sends the code for Email/SMS) then sign.
 */
export function SignReportDialog(props: {
  stage: 'validation' | 'approval';
  reportingOid: string;
  reportId: string;
  secondFactor: string;                       // Mfa | Email | Sms (from app-config)
  onClose: () => void;
  onSigned: (meaning: string) => void;
}) {
  const { stage, reportingOid, reportId, onClose, onSigned } = props;
  const meaning = stage === 'validation' ? 'Validated' : 'Approved';
  const verb = stage === 'validation' ? 'Validate' : 'Approve';

  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [factor, setFactor] = useState<string>(props.secondFactor);
  const [maskedDest, setMaskedDest] = useState<string | null>(null);
  const [certified, setCertified] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // First-time MFA: enrol inline (scan QR) instead of being turned away.
  const [enrollMode, setEnrollMode] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [enrollSecret, setEnrollSecret] = useState('');
  const [enrollCode, setEnrollCode] = useState('');
  const [enrollBusy, setEnrollBusy] = useState(false);
  // Service/admin only: may re-display the QR to (re-)scan an authenticator even
  // when already enrolled (set from the challenge response).
  const [canEnrollMfa, setCanEnrollMfa] = useState(false);
  // Service/admin MFA not enrolled yet — show the dialog + "Show QR code" link
  // (the QR is NOT shown automatically; only when the user clicks it).
  const [mfaNeedsSetup, setMfaNeedsSetup] = useState(false);

  const apiErr = (e: unknown, fallback: string) =>
    (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? fallback;
  const apiCode = (e: unknown) =>
    (e as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;

  // Start MFA enrollment: server mints a fresh secret; render its QR to scan.
  const beginEnroll = useCallback(async () => {
    try {
      // Sign-scoped enrolment — stores a SIGNING secret only; does NOT enable login
      // MFA, so the user's login stays password-only (separate from /auth/mfa/enroll).
      const res = await api.post('/api/v1/reporting/sign/mfa/enroll', {});
      const d = (res.data as { data: { secretBase32: string; otpAuthUri: string } }).data;
      setEnrollSecret(d.secretBase32);
      setQr(await QRCode.toDataURL(d.otpAuthUri, { errorCorrectionLevel: 'M', margin: 1, width: 200 }));
      setEnrollMode(true);
    } catch (e) {
      setErr(apiErr(e, 'Could not start MFA enrollment.'));
    }
  }, []);

  const requestChallenge = useCallback(async () => {
    setErr(null); setBusy(true);
    try {
      const res = await api.post(`/api/v1/reporting/${stage}/${reportingOid}/sign/challenge`, {});
      const d = (res.data as { data: { challengeId: string; factor: string; destinationMasked: string | null; canEnrollMfa?: boolean } }).data;
      setChallengeId(d.challengeId);
      setFactor(d.factor);
      setMaskedDest(d.destinationMasked ?? null);
      setCanEnrollMfa(d.canEnrollMfa === true);
      setMfaNeedsSetup(false);
      setEnrollMode(false);
    } catch (e) {
      // Service/admin not enrolled: do NOT auto-open the QR — surface the dialog with
      // the "Show QR code" link so the QR appears ONLY when the user clicks it.
      if (apiCode(e) === 'MfaNotEnrolled') {
        setFactor('Mfa');
        setCanEnrollMfa(true);
        setMfaNeedsSetup(true);
      } else {
        setErr(apiErr(e, 'Could not start the signing session.'));
      }
    } finally {
      setBusy(false);
    }
  }, [stage, reportingOid]);

  useEffect(() => { void requestChallenge(); }, [requestChallenge]);

  // Confirm the scanned authenticator code → persist the secret → continue.
  const confirmEnroll = async () => {
    if (!/^\d{6}$/.test(enrollCode.trim())) { setErr('Enter the 6-digit code from your authenticator app.'); return; }
    setEnrollBusy(true); setErr(null);
    try {
      await api.post('/api/v1/reporting/sign/mfa/enroll/verify',
        { secretBase32: enrollSecret, code: enrollCode.trim() });
      setEnrollCode('');
      await requestChallenge();   // now enrolled → real MFA challenge, back to the sign form
    } catch (e) {
      setErr(apiErr(e, 'That code did not match — try the latest one from your app.'));
    } finally {
      setEnrollBusy(false);
    }
  };

  const isOtp = factor === 'Email' || factor === 'Sms';
  const codeLabel = factor === 'Mfa' ? 'Authenticator code' : factor === 'Sms' ? 'SMS code' : 'Email code';

  const doSign = async () => {
    if (!certified) { setErr('You must check the certification statement to sign.'); return; }
    if (!password) { setErr('Re-enter your password to sign.'); return; }
    if (!/^\d{6}$/.test(code.trim())) { setErr('Enter the 6-digit verification code.'); return; }
    if (!challengeId) { setErr('Signing session not ready — please retry.'); return; }
    setErr(null); setBusy(true);
    try {
      await api.post(`/api/v1/reporting/${stage}/${reportingOid}/sign`,
        { challengeId, password, code: code.trim(), certified: true });
      onSigned(meaning);
    } catch (e) {
      setErr(apiErr(e, 'Signing failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={scrim} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={head}>
          <i className="ti ti-signature" />&nbsp;Electronic Signature — {verb}
          <span style={{ marginLeft: 'auto', cursor: 'pointer' }} onClick={onClose}>✕</span>
        </div>

        <div style={{ padding: 18 }}>
          {enrollMode ? (
            <div style={ctx}>
              <div style={{ fontWeight: 700, color: 'var(--ai-ink)', marginBottom: 6 }}>
                <i className="ti ti-qrcode" />&nbsp;Set up your authenticator (first time)
              </div>
              <div style={{ fontSize: 12, color: 'var(--ai-muted)', marginBottom: 12 }}>
                Scan this QR with Google Authenticator, Microsoft Authenticator or Authy, then enter
                the 6-digit code it shows to finish setup and continue signing.
              </div>
              {qr && (
                <div style={{ textAlign: 'center', marginBottom: 12 }}>
                  <img src={qr} alt="Authenticator QR" width={180} height={180}
                       style={{ border: '1px solid var(--ai-line)', borderRadius: 8 }} />
                </div>
              )}
              <div style={fld}>
                <label style={lbl}>Authenticator code</label>
                <input value={enrollCode} onChange={(e) => setEnrollCode(e.target.value)} placeholder="000000"
                       maxLength={6} inputMode="numeric" autoComplete="off" style={input} />
              </div>
            </div>
          ) : (<>
          <div style={ctx}>
            <Row k="Report" v={reportId} />
            <Row k="Signature meaning" v={<span style={meaningPill}>✔ {meaning}</span>} />
            <Row k="Second factor" v={factor === 'Mfa' ? 'Authenticator (MFA)' : factor === 'Sms' ? 'SMS code' : 'Email code'} />
          </div>

          <label style={attest}>
            <input type="checkbox" checked={certified} onChange={(e) => setCertified(e.target.checked)}
                   style={{ marginTop: 2, width: 16, height: 16 }} />
            <span>I certify that I have reviewed this report and that this electronic signature is the
              legally binding equivalent of my handwritten signature.
              <span style={{ color: 'var(--ai-muted)' }}> (ISO/IEC 17025 · 21 CFR Part 11 §11.200)</span></span>
          </label>

          <div style={fld}>
            <label style={lbl}>Re-enter password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   placeholder="••••••••" autoComplete="off" style={input} />
          </div>

          <div style={fld}>
            <label style={lbl}>{codeLabel}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000"
                     maxLength={6} inputMode="numeric" autoComplete="off" style={{ ...input, flex: 1 }} />
              {isOtp && (
                <button onClick={() => void requestChallenge()} disabled={busy}
                        style={resendBtn} title="Send a new code">
                  {maskedDest ? 'Resend' : 'Send code'}
                </button>
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginTop: 3 }}>
              {factor === 'Mfa'
                ? (mfaNeedsSetup
                    ? 'Authenticator not set up yet — click "Show QR code" below to set it up, then enter the code.'
                    : 'Enter the 6-digit code from your authenticator app.')
                : maskedDest ? `Code sent to ${maskedDest}.` : 'Click "Send code" to receive your one-time code.'}
            </div>
            {/* Service/admin: re-display the QR to (re-)scan an authenticator and
                read the OTP — handy in the demo even when already enrolled. */}
            {factor === 'Mfa' && canEnrollMfa && (
              <button type="button" onClick={() => void beginEnroll()} style={qrLinkBtn}>
                <i className="ti ti-qrcode" />&nbsp;Show QR code — scan to set up the authenticator &amp; read the code
              </button>
            )}
          </div>
          </>)}

          {err && <div style={errBox}>{err}</div>}
        </div>

        <div style={foot}>
          <button onClick={onClose} style={btn} disabled={busy || enrollBusy}>Cancel</button>
          {enrollMode ? (
            <button onClick={() => void confirmEnroll()} style={{ ...btn, ...btnPrimary }} disabled={enrollBusy}>
              <i className="ti ti-qrcode" />&nbsp;{enrollBusy ? 'Verifying…' : 'Verify & continue'}
            </button>
          ) : (
            <button onClick={() => void doSign()} style={{ ...btn, ...btnPrimary }} disabled={busy}>
              <i className="ti ti-signature" />&nbsp;{busy ? 'Signing…' : `${verb} & Sign`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
      <span style={{ color: 'var(--ai-muted)' }}>{k}</span><b>{v}</b>
    </div>
  );
}

const scrim: CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,18,28,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 };
const modal: CSSProperties = { background: '#fff', borderRadius: 10, width: 540, maxWidth: '94vw', boxShadow: '0 8px 28px rgba(20,24,40,.18)', overflow: 'hidden' };
const head: CSSProperties = { background: 'var(--brand-accent, var(--ai-orange))', color: '#fff', padding: '13px 18px', display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: 14.5 };
// token-exempt: #fdf8f4 = subtle brand-tinted panel (no matching --ai-* token).
const ctx: CSSProperties = { background: '#fdf8f4', border: '1px solid var(--ai-line)', borderRadius: 8, padding: '12px 14px', marginBottom: 14, fontSize: 12.5 };
// token-exempt: semantic blue "signature meaning" pill (no matching --ai-* token).
const meaningPill: CSSProperties = { background: '#eef4fb', color: '#1c4f8b', border: '1px solid #c8ddf3', borderRadius: 20, padding: '2px 10px', fontWeight: 700, fontSize: 11.5 };
const attest: CSSProperties = { display: 'flex', gap: 9, alignItems: 'flex-start', background: 'var(--ai-header-bg)', border: '1px solid var(--ai-line)', borderRadius: 8, padding: '11px 12px', marginBottom: 14, fontSize: 12, color: 'var(--ai-ink)', lineHeight: 1.45 };
const fld: CSSProperties = { marginBottom: 12 };
const lbl: CSSProperties = { display: 'block', fontSize: 12, color: 'var(--ai-muted)', marginBottom: 4, fontWeight: 600 };
const input: CSSProperties = { width: '100%', padding: '9px 11px', border: '1px solid var(--ai-line)', borderRadius: 7, fontSize: 13 };
const resendBtn: CSSProperties = { border: '1px solid var(--brand-accent, var(--ai-orange))', background: '#fff', color: 'var(--brand-accent-hover, var(--ai-orange-hover))', borderRadius: 7, padding: '0 12px', fontWeight: 700, fontSize: 12, cursor: 'pointer' };
const qrLinkBtn: CSSProperties = { marginTop: 8, display: 'inline-flex', alignItems: 'center', background: 'none', border: 'none', padding: 0, color: 'var(--brand-accent-hover, var(--ai-orange-hover))', fontWeight: 600, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' };
// token-exempt: semantic error-red tint pair (no matching --ai-* token).
const errBox: CSSProperties = { color: 'var(--ai-red)', background: '#fdeceb', border: '1px solid #f5c6c2', borderRadius: 7, padding: '8px 11px', fontSize: 12.5 };
const foot: CSSProperties = { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 18px', borderTop: '1px solid var(--ai-line)', background: 'var(--ai-header-bg)' };
const btn: CSSProperties = { border: '1px solid var(--ai-line)', background: '#fff', borderRadius: 6, padding: '7px 14px', fontSize: 12.5, cursor: 'pointer' };
const btnPrimary: CSSProperties = { background: 'var(--ai-notify-success)', borderColor: 'var(--ai-notify-success)', color: '#fff', fontWeight: 700 };
